import { useMemo } from 'react'

import { setupConnectionQualityIndicator } from '@livekit/components-core'
import type { Participant } from 'livekit-client'
import { ConnectionQuality } from 'livekit-client'

import { useEnsureParticipant } from '@/src/context'
import { useObservableState } from '@/src/hooks/internal'

/** @public */
export interface ConnectionQualityIndicatorOptions {
  participant?: Participant
}

/**
 * The `useConnectionQualityIndicator` hook provides props for the `ConnectionQualityIndicator` or your custom implementation of it component.
 * @example
 * ```tsx
 * const { quality } = useConnectionQualityIndicator();
 * // or
 * const { quality } = useConnectionQualityIndicator({ participant });
 * ```
 * @public
 */
export function useConnectionQualityIndicator(options: ConnectionQualityIndicatorOptions = {}) {
  const p = useEnsureParticipant(options.participant)

  const { className, connectionQualityObserver } = useMemo(
    () => setupConnectionQualityIndicator(p),
    [p],
  )

  const quality = useObservableState(connectionQualityObserver, ConnectionQuality.Unknown)

  return { className, quality }
}
